import { Container } from '@mui/material'
import { useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import BoltRoundedIcon from '@mui/icons-material/BoltRounded'
import { UserRpps } from '@saas/interfaces/core'
import {
    GetHcsAnnContactsResponseIds,
    getIdsAnnDirectoryPhysicianRppsRppsProfessionCodeProfession,
} from '@toktokdoctech/tokapi-client'
import useUserPermissions from '../../../hooks/useUserPermissions'
import { capitalizeFirstLetter } from '../../../utils/stringUtils'
import CommonLoaderBackdrop from '../../Common/CommonLoaderBackdrop'
import DeletionBanner from '../../Common/DeletionBanner/DeletionBanner'
import FlexBox from '../../Common/flexBox/FlexBox'
import FormSection from '../../Common/form/formSection/FormSection'
import SaveComponent from '../../Common/SaveComponent/SaveComponent'
import SearchRpps from '../../Common/searchRpps/SearchRpps'
import { useContactFormContext } from '../context/ContactFormContext'
import { ContactFormProps } from './_props'
import useStyles from './_styles/ContactFormStyles'
import Category from './fields/Category'
import Firstname from './fields/FirstName'
import LastName from './fields/LastName'
import NationalId from './fields/NationalId'
import Profession from './fields/Profession'
import RppsId from './fields/RppsId'
import SecuredEmail from './fields/SecuredEmail'
import Speciality from './fields/Speciality'
import useContactServices from './services/useContactServices'
import { rppsDefaultData } from '../../User/utils/_utils'
import FormContainer from '../../Common/form/formContainer/FormContainer'
import Tag from '../../Common/Tag/Tag'
import { defaultPalette } from '../../../theme/idsTheme'

const ContactForm = ({ data }: ContactFormProps) => {
    const classes = useStyles()

    const navigate = useNavigate()
    const { hasUserPermission } = useUserPermissions()

    const { state } = useContactFormContext()

    const [isSearchingRppsUser, setIsSearchingRppsUser] = useState(false)
    const [selectedRppsUser, setSelectecRppsUser] = useState<UserRpps>()
    const [hasError, setHasError] = useState(false)
    const [errorMessage, setErrorMessage] = useState(
        'Veuillez corriger tous les champs comportant des erreurs pour pouvoir enregistrer.',
    )
    const [shouldBlockNavigation, setShouldBlockNavigation] = useState(false)

    const form = useForm<GetHcsAnnContactsResponseIds>({
        mode: 'onChange',
        defaultValues: data,
    })

    const { handleSubmit, reset } = form

    const { handleCreateContact, handleDeleteContact, isDeletingContact, isAddingContact } = useContactServices({
        data,
    })

    const loaderMessage = useMemo(() => {
        if (isAddingContact) return 'Enregistrement des informations du correspondant...'
        if (isSearchingRppsUser) return 'Recherche des informations du correspondant...'
        return undefined
    }, [isAddingContact, isSearchingRppsUser])

    const handleCancel = () => {
        setShouldBlockNavigation(false)
        navigate('/contacts')
    }

    const handleSave = async () => {
        setShouldBlockNavigation(false)
        try {
            if (state.mode === 'CREATION') {
                await handleSubmit(handleCreateContact)()
                navigate('/contacts')
            }
        } catch (err) {
            setErrorMessage(
                'Ce correspondant est déjà existant dans la liste.\nVous pouvez “annuler” pour quitter la saisie du formulaire.',
            )
            setHasError(true)
        }
    }

    const onSelectUserRpps = async (rppsUser: UserRpps | string | null) => {
        if (rppsUser && typeof rppsUser !== 'string') {
            setIsSearchingRppsUser(true)
            setSelectecRppsUser(rppsUser)

            const userAnnData = await getIdsAnnDirectoryPhysicianRppsRppsProfessionCodeProfession(
                String(rppsUser.identifiantpp),
                String(rppsUser.codeprofession),
            )

            reset({
                lastName: rppsUser.nomdexercice.toUpperCase(),
                firstName: capitalizeFirstLetter(rppsUser.prenomdexercice),
                profession: userAnnData.profession?.name ?? '',
                speciality: userAnnData.speciality?.name ?? '',
                rppsNum: String(rppsUser.identifiantpp),
                nationalId: userAnnData?.userIdnps ?? '',
                category: userAnnData?.userRole || '',
                securedEmail: userAnnData?.mssEmails ? userAnnData?.mssEmails[0] : '',
            })

            setIsSearchingRppsUser(false)
            setShouldBlockNavigation(true)
        } else {
            setSelectecRppsUser(undefined)
            reset(rppsDefaultData)
            setShouldBlockNavigation(false)
        }
    }

    const clearError = () => {
        if (hasError) {
            setHasError(false)
        }
    }

    return (
        <>
            <FormProvider {...form}>
                <FormContainer className={classes.ContactFormProvider}>
                    <Container className={classes.ContactFormContainer}>
                        <FlexBox flexDirection="column" gap="2.125rem">
                            {state.mode === 'CREATION' && (
                                <SearchRpps
                                    displayTitle
                                    onRppsUserChange={onSelectUserRpps}
                                    onAutocompleteSelected={clearError}
                                    required
                                    helperTextPosition="start"
                                    error={hasError && !selectedRppsUser}
                                    errorMessage="Cette information est obligatoire et n’a pas été renseignée"
                                    onClear={clearError}
                                />
                            )}
                            <FormSection
                                label="Informations du correspondant"
                                labelSideComponent={
                                    state.mode === 'EDITION' &&
                                    data?.isAutoCreation && (
                                        <Tag
                                            background={defaultPalette.primary.ultraLighter}
                                            color={defaultPalette.primary.main}
                                            icon={<BoltRoundedIcon />}
                                            label="Créé automatiquement"
                                            rounded
                                        />
                                    )
                                }
                            >
                                <LastName />
                                <Firstname />
                                <Profession />
                                <Speciality />
                                <RppsId />
                                <NationalId />
                                <Category />
                                <SecuredEmail />
                            </FormSection>
                        </FlexBox>
                        {state.mode === 'CREATION' && (
                            <SaveComponent
                                saveButton={{
                                    action: () => {
                                        if (!selectedRppsUser) {
                                            setHasError(true)
                                        } else {
                                            handleSave()
                                        }
                                    },
                                    displayed: hasUserPermission('USER'),
                                    isLoading: isAddingContact,
                                }}
                                cancelButton={{ action: handleCancel }}
                                disableAll={false}
                                shouldBlockNavigation={shouldBlockNavigation}
                                errorMessage={hasError ? errorMessage : undefined}
                            />
                        )}
                    </Container>
                    {state.mode === 'EDITION' && !data?.isAutoCreation && (
                        <DeletionBanner
                            bannerTitle="Êtes-vous sûr de vouloir supprimer le correspondant ?"
                            bannerLabel="Supprimer la fiche correspondant"
                            bannerSubLabel="Après confirmation, la suppression sera définitive"
                            onConfirm={handleDeleteContact}
                            isPending={isDeletingContact}
                        />
                    )}
                </FormContainer>
            </FormProvider>
            {loaderMessage && <CommonLoaderBackdrop open label={loaderMessage} />}
        </>
    )
}

export default ContactForm
